/**
 * Maps GetCompleteCatalog API response to the editor catalog shape.
 * GetCompleteCatalog schema: id, name, vpatEdition, description, standards[], isActive, createdDate, lastModifiedDate.
 * Conformance levels are not in that response; they come from GET /api/catalog/conformance-levels
 * and are set on the catalog in About.page.jsx after this transform.
 *
 * API standards[]: each has id, name, version, description, chapters[] (name, description, criteria[]).

* Criteria: id, code, name, description, url, components: string[].
 * Editor output: standards[], chapters[], components[], conformanceLevels: [].
 *
 * WCAG: When multiple standards are WCAG (name contains "WCAG"), chapters with the same name are combined
 * (e.g. Level A from 2.0, 2.1, 2.2 → one "Level A" chapter). Criteria sorted by code. Non-WCAG standards keep separate chapters.
 *
 * Criteria with no components (e.g. non-WCAG from DB): assigned a single default component so Level and Remarks still render.
 */
function isWcagStandard(standard) {
  if (!standard || typeof standard !== "object") return false;
  const blob = `${standard.label ?? ""} ${standard.name ?? ""} ${standard.id ?? ""}`.toLowerCase();
  return blob.includes("wcag") || blob.includes("web content accessibility guidelines");
}

const WCAG_CHAPTER_ORDER = ["Level A", "Level AA", "Level AAA"];

/**
 * Display-friendly short labels for chapters whose API names are long.
 * Keyed by the exact chapter name returned by the API.
 */
const SHORT_LABEL_OVERRIDES = {
  // WCAG
  "Level A": "A",
  "Level AA": "AA",
  "Level AAA": "AAA",
  // Section 508
  "Functional Performance Criteria": "Functional",
  "Support Documentation and Services": "Documentation",
  // EN 301 549
  "Functional Performance": "Functional",
  "Generic Requirements": "Generic",
  "ICT With Two-way Voice Communication": "ICT - Voice",
  "ICT With Video Capabilities": "ICT - Video",
  "Non-Web Documents": "Non-Web",
  "Documentation and Support Services": "Documentation",
  "ICT Providing Relay or Emergency Service Access": "ICT - Emergency",
};

function toShortLabel(label) {
  return SHORT_LABEL_OVERRIDES[label] ?? label;
}

/** Used when the API returns a criterion with no components so the editor still shows Level and Remarks. */
export const DEFAULT_CRITERIA_COMPONENT_ID = "none";

/**
 * Returns a catalog with every criterion guaranteed to have at least one component (for Level/Remarks UI).
 * Use when returning a catalog from cache so cached catalogs behave like freshly transformed ones.
 * Does not mutate the input; returns a new structure when normalization is needed.
 */
export function normalizeCatalogCriteriaComponents(catalog) {
  const catalogForNorm = catalog;
  if (!catalogForNorm?.chapters?.length) return catalogForNorm;
  const components = Array.isArray(catalogForNorm.components) ? [...catalogForNorm.components] : [];
  let needsDefaultComponent = false;
  let needsShortLabelNorm = false;
  const chapters = catalogForNorm.chapters.map((chapter) => {
    const normalizedShortLabel = toShortLabel(chapter.label ?? chapter.short_label ?? "");
    if (normalizedShortLabel !== chapter.short_label) needsShortLabelNorm = true;
    return {
      ...chapter,
      short_label: normalizedShortLabel,
      criteria: (chapter.criteria || []).map((criterion) => {
        const comps = criterion.components;
        if (!Array.isArray(comps) || comps.length === 0) {
          needsDefaultComponent = true;
          return { ...criterion, components: [{ name: DEFAULT_CRITERIA_COMPONENT_ID }] };
        }
        return criterion;
      }),
    };
  });
  if (needsDefaultComponent) {
    const hasDefault = components.some(
      (c) => (c?.id ?? c) === DEFAULT_CRITERIA_COMPONENT_ID
    );
    if (!hasDefault) {
      components.push({
        id: DEFAULT_CRITERIA_COMPONENT_ID,
        label: DEFAULT_CRITERIA_COMPONENT_ID,
      });
    }
  }
  if (!needsDefaultComponent && !needsShortLabelNorm) return catalogForNorm;
  return { ...catalogForNorm, chapters, components };
}

function parseCriteriaSortKey(name) {
  if (!name || typeof name !== "string") return [0];
  const match = name.trim().match(/^(\d+(?:\.\d+)*)/);
  if (!match) return [0];
  return match[1].split(".").map(Number);
}

function compareCriteriaByTitle(a, b) {
  const keyA = parseCriteriaSortKey(a.id ?? "")[0] ? parseCriteriaSortKey(a.id) : parseCriteriaSortKey(a.handle ?? a.name ?? "");
  const keyB = parseCriteriaSortKey(b.id ?? "")[0] ? parseCriteriaSortKey(b.id) : parseCriteriaSortKey(b.handle ?? b.name ?? "");
  const len = Math.max(keyA.length, keyB.length);
  for (let i = 0; i < len; i++) {
    const va = keyA[i] ?? 0;
    const vb = keyB[i] ?? 0;
    if (va !== vb) return va - vb;
  }
  return 0;
}

function wcagChapterId(chapterName) {
  const slug = (chapterName ?? "").replace(/\s+/g, "_").toLowerCase();
  return `wcag_${slug}`;
}

/** URL-friendly id from chapter label for non-WCAG chapters (e.g. "Functional Performance Criteria" -> "functional_performance_criteria"). */
function labelToChapterSlug(label) {
  if (!label || typeof label !== "string") return "";
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

function wcagVersionFromText(text) {
  const asText = String(text ?? "");
  const wcagMatch = asText.match(/wcag[\s_-]*([0-9]+(?:[._][0-9]+)?)/i);
  if (wcagMatch?.[1]) return wcagMatch[1].replace(/_/g, ".");
  const genericVersionMatch = asText.match(/\b([0-9]+(?:[._][0-9]+)?)\b/);
  return genericVersionMatch?.[1]?.replace(/_/g, ".") ?? "";
}

function compareVersionStrings(a, b) {
  const aParts = String(a || "0").split(".").map((v) => Number(v));
  const bParts = String(b || "0").split(".").map((v) => Number(v));
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i += 1) {
    const av = aParts[i] ?? 0;
    const bv = bParts[i] ?? 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

function wcagVersionForStandard(standard) {
  return [
    standard?.version,
    standard?.label,
    standard?.name,
    standard?.id,
  ]
    .map(wcagVersionFromText)
    .find(Boolean) ?? "";
}

function normalizeWcagUrlForVersion(url, version) {
  const normalizedVersion = String(version ?? "").replace(/[._]/g, "");
  if (!url || !normalizedVersion) return url;
  return String(url)
    .replace(/\/WAI\/WCAG[0-9]{2}\//gi, `/WAI/WCAG${normalizedVersion}/`)
    .replace(/\/TR\/WCAG[0-9]{2}\//gi, `/TR/WCAG${normalizedVersion}/`)
    .replace(/\/WAI\/WCAG[0-9]{2}(\/|#|\?|$)/gi, `/WAI/WCAG${normalizedVersion}$1`)
    .replace(/\/TR\/WCAG[0-9]{2}(\/|#|\?|$)/gi, `/TR/WCAG${normalizedVersion}$1`);
}

function wcagStandardUrlForVersion(version, fallbackUrl = "") {
  const normalizedVersion = String(version ?? "").replace(/_/g, ".");
  if (normalizedVersion === "2.0") return "https://www.w3.org/TR/WCAG20/";
  if (normalizedVersion === "2.1") return "https://www.w3.org/TR/WCAG21/";
  if (normalizedVersion === "2.2") return "https://www.w3.org/TR/WCAG22/";
  return normalizeWcagUrlForVersion(fallbackUrl, normalizedVersion) || fallbackUrl;
}

function wcagLabelForStandard(standard) {
  const version = wcagVersionForStandard(standard);
  if (version) return `WCAG ${version}`;
  return standard?.name ?? standard?.id ?? "WCAG";
}

export default function transformCompleteCatalogToEditor(apiCatalog) {
  if (!apiCatalog?.standards?.length) {
    return { standards: [], chapters: [], components: [], conformanceLevels: [] };
  }

  const wcagVersionsInCatalog = apiCatalog.standards
    .filter((standard) => isWcagStandard(standard))
    .map((standard) => wcagVersionForStandard(standard))
    .filter(Boolean);
  const primaryWcagVersion = wcagVersionsInCatalog.reduce(
    (best, current) => (compareVersionStrings(current, best) > 0 ? current : best),
    ""
  );

  const componentNames = new Set();
  const wcagChaptersByName = new Map();
  const nonWcagChapters = [];
  const wcagChapterIdsInOrder = [];

  apiCatalog.standards.forEach((standard) => {
    const isWcag = isWcagStandard(standard);
    const standardWcagVersion = isWcag ? wcagVersionForStandard(standard) : "";
    const wcagGuidanceVersion = primaryWcagVersion || standardWcagVersion;
    const standardUrl = isWcag
      ? wcagStandardUrlForVersion(standardWcagVersion, standard.url ?? "")
      : (standard.url ?? "");

    (standard.chapters || []).forEach((chapter, chIndex) => {
      const criteria = (chapter.criteria || []).map((c) => {
        const apiComponents = c.components || [];
        const criterionUrl = isWcag
          ? normalizeWcagUrlForVersion(c.url || null, wcagGuidanceVersion)
          : (c.url || null);
        if (apiComponents.length === 0) {
          componentNames.add(DEFAULT_CRITERIA_COMPONENT_ID);
          return {
            id: c.code,
            alt_id: c.code,
            handle: c.name ?? c.code,
            url: criterionUrl,
            components: [{ name: DEFAULT_CRITERIA_COMPONENT_ID }],
          };
        }
        apiComponents.forEach((name) => componentNames.add(name));
        return {
          id: c.code,
          alt_id: c.code,
          handle: c.name ?? c.code,
          url: criterionUrl,
          components: apiComponents.map((name) => ({ name })),
        };
      });

      const label = chapter.name ?? `Chapter ${chIndex + 1}`;
      const chapterDescription = chapter.description ?? "";
      const rawChapterUrl = chapter.url || standardUrl || "";
      const chapterUrl = isWcag
        ? wcagStandardUrlForVersion(wcagGuidanceVersion, rawChapterUrl)
        : rawChapterUrl;

      if (isWcag) {
        const existing = wcagChaptersByName.get(label);
        if (existing) {
          const existingCodes = new Set(existing.criteria.map((cr) => cr.id));
          criteria.forEach((cr) => {
            if (!existingCodes.has(cr.id)) {
              existingCodes.add(cr.id);
              existing.criteria.push(cr);
            }
          });
          if (!existing.description && chapterDescription) {
            existing.description = chapterDescription;
          }
          if (!existing.url && chapterUrl) {
            existing.url = chapterUrl;
          }
        } else {
          wcagChaptersByName.set(label, {
            label,
            description: chapterDescription,
            url: chapterUrl,
            criteria: [...criteria],
          });
        }
      } else {
        const baseId = labelToChapterSlug(label) || `chapter_${chIndex}`;
        const used = new Set(nonWcagChapters.map((c) => c.id));
        let chapterId = baseId;
        let n = 2;
        while (used.has(chapterId)) {
          chapterId = `${baseId}_${n}`;
          n += 1;
        }
        nonWcagChapters.push({
          id: chapterId,
          label,
          description: chapterDescription,
          url: chapterUrl,
          short_label: toShortLabel(label),
          standardId: standard.id,
          criteria,
        });
      }
    });
  });

  const chapters = [];
  const wcagNamesOrdered = [
    ...WCAG_CHAPTER_ORDER.filter((n) => wcagChaptersByName.has(n)),
    ...Array.from(wcagChaptersByName.keys()).filter((n) => !WCAG_CHAPTER_ORDER.includes(n)),
  ];

  wcagNamesOrdered.forEach((chapterName) => {
    const entry = wcagChaptersByName.get(chapterName);
    if (!entry) return;
    const id = wcagChapterId(chapterName);
    wcagChapterIdsInOrder.push(id);
    entry.criteria.sort(compareCriteriaByTitle);
    chapters.push({
      id,
      label: entry.label,
      description: entry.description ?? "",
      url: entry.url ?? "",
      short_label: toShortLabel(entry.label),
      criteria: entry.criteria,
    });
  });

  nonWcagChapters.forEach((ch) => {
    chapters.push({
      id: ch.id,
      label: ch.label,
      description: ch.description ?? "",
      url: ch.url ?? "",
      short_label: ch.short_label,
      criteria: ch.criteria,
    });
  });

  const standards = apiCatalog.standards.map((standard) => {
    const isWcag = isWcagStandard(standard);
    const standardWcagVersion = isWcag ? wcagVersionForStandard(standard) : "";
    const standardUrl = isWcag
      ? wcagStandardUrlForVersion(standardWcagVersion, standard.url ?? "")
      : (standard.url ?? "");
    const chapterIds = isWcag
      ? [...wcagChapterIdsInOrder]
      : nonWcagChapters.filter((ch) => ch.standardId === standard.id).map((ch) => ch.id);
    const label = isWcag
      ? wcagLabelForStandard(standard)
      : (`${standard.name ?? ""} ${standard.version ?? ""}`.trim() || standard.id);
    return {
      id: standard.id,
      label,
      url: standardUrl,
      chapters: chapterIds,
    };
  });

  const components = Array.from(componentNames).map((name) => ({ id: name, label: name }));

  return {
    id: apiCatalog.id,
    name: apiCatalog.name ?? "",
    title: apiCatalog.name ?? "",
    label: apiCatalog.name ?? "",
    standards,
    chapters,
    components,
    conformanceLevels: [],
  };
}
